import { useState, useRef } from 'react';
import { productsApi, type Product } from '../lib/api';

// Slide-over «Новый товар» — recreated from the Claude Design handoff.
// Minimal required fields (name + price), supportive hints, optional photo.
// On save it creates the product via the API, optionally uploads the chosen
// photo, then hands the created product back to the list.

type Props = {
  sellerId: number;
  onClose: () => void;
  onCreated: (p: Product, again: boolean) => void;
};

// Generate a collision-resistant default SKU when the operator leaves the
// «Артикул» field blank. The DB enforces UNIQUE(seller_id, external_offer_id),
// so a 90-value space ("PR10".."PR99") would 409 almost immediately on
// "Сохранить и добавить ещё". Use timestamp + randomness in base36, which
// also satisfies the server regex /^[A-Za-z0-9_\-.()]+$/.
function genCode(): string {
  const ts = Date.now().toString(36).toUpperCase().slice(-5);
  const rnd = Math.floor(Math.random() * 36 * 36).toString(36).toUpperCase().padStart(2, '0');
  return `PR-${ts}${rnd}`;
}

export default function NewProductPanel({ sellerId, onClose, onCreated }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '', code: '', height: '',
    price: '', stock: '', packQty: '', packUnit: 'шт', minOrder: '1', active: true,
  });
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: string, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  const nameOk = form.name.trim().length > 1;
  const priceOk = form.price !== '' && Number(form.price) > 0;
  const canSave = nameOk && priceOk && !busy;

  function pickPhoto(f: File | null) {
    setPhoto(f);
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    setPhotoUrl(f ? URL.createObjectURL(f) : null);
  }

  async function trySave(again: boolean) {
    setTouched(true);
    if (!nameOk || !priceOk) return;
    setBusy(true);
    setErr(null);
    try {
      const props: Record<string, unknown> = {};
      if (form.height.trim()) props.height = form.height.trim();
      const created = await productsApi.create({
        seller_id: sellerId,
        external_offer_id: form.code.trim() || genCode(),
        name: form.name.trim(),
        price_rub: Math.round(Number(form.price)), // server rejects non-integer (zod .int())
        stock_qty: form.stock === '' ? null : Number(form.stock),
        bunch_size: form.packQty === '' ? null : Number(form.packQty),
        min_order_count: Math.max(1, Number(form.minOrder) || 1),
        is_active: form.active,
        properties: props,
      });
      let product = created.product;
      // Optional photo: upload after create so it shares the product's id/dir.
      if (photo) {
        try {
          const r = await productsApi.uploadImages(product.id, [photo]);
          product = { ...product, images: r.images };
        } catch { /* photo failure shouldn't block product creation */ }
      }
      onCreated(product, again);
      if (again) {
        // Keep category/active, clear the per-item fields for the next entry.
        setForm((f) => ({ ...f, name: '', code: '', height: '', price: '', stock: '' }));
        pickPhoto(null);
        setTouched(false);
      }
    } catch (e) {
      const msg = String((e as { message?: string })?.message ?? e);
      setErr(msg === 'duplicate_offer_id' ? 'Такой артикул уже есть — укажите другой' : msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="overlay" onClick={onClose}></div>
      <aside className="slideover" role="dialog" aria-label="Новый товар">
        <div className="panel-head">
          <h2>Новый товар</h2>
          <button type="button" className="iconbtn" title="Закрыть" onClick={onClose}>✕</button>
        </div>
        <div className="panel-body">
          <div className="dropzone" title="Фото можно добавить и позже" onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) pickPhoto(f); }}>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={(e) => pickPhoto(e.target.files?.[0] ?? null)} />
            {photoUrl ? (
              <img src={photoUrl} alt="" className="thumb-preview" />
            ) : (
              <>
                <span className="big">＋</span>
                <span>Перетащите фото сюда или нажмите</span>
                <span className="mono">jpg · png · до 40 МБ — можно позже</span>
              </>
            )}
          </div>

          <div className="field">
            <label>Название <span className="req">*</span></label>
            <input type="text" placeholder="Например: Роза Пинк Флойд" value={form.name}
              className={touched && !nameOk ? 'invalid' : ''} autoFocus
              onChange={(e) => set('name', e.target.value)} />
            {touched && !nameOk ? <span className="hint" style={{ color: 'var(--danger)' }}>Укажите название — его увидят покупатели</span> : null}
          </div>

          <div className="field-row">
            <div className="field">
              <label>Артикул</label>
              <input type="text" placeholder="авто" value={form.code} onChange={(e) => set('code', e.target.value)} />
            </div>
            <div className="field">
              <label>Высота, см</label>
              <input type="number" min="0" placeholder="70" value={form.height} onChange={(e) => set('height', e.target.value)} />
            </div>
          </div>

          <div className="field-row">
            <div className="field">
              <label>Цена за шт, ₽ <span className="req">*</span></label>
              <input type="number" min="0" placeholder="0" value={form.price}
                className={touched && !priceOk ? 'invalid' : ''}
                onChange={(e) => set('price', e.target.value)} />
            </div>
            <div className="field">
              <label>Наличие, шт</label>
              <input type="number" min="0" placeholder="—" value={form.stock} onChange={(e) => set('stock', e.target.value)} />
              <span className="hint">Пусто — товар попадёт в черновики</span>
            </div>
          </div>

          <div className="field-row">
            <div className="field">
              <label>В упаковке, шт</label>
              <input type="number" min="0" placeholder="10" value={form.packQty} onChange={(e) => set('packQty', e.target.value)} />
            </div>
            <div className="field">
              <label>Мин. заказ, упак.</label>
              <input type="number" min="1" value={form.minOrder} onChange={(e) => set('minOrder', e.target.value)} />
            </div>
          </div>

          <div className="toggle-row">
            <span className="lbl">
              <b>Сразу показать покупателям</b>
              <span>Можно выключить и доработать карточку позже</span>
            </span>
            <button type="button" className={'switch' + (form.active ? ' on' : '')} role="switch"
              aria-checked={form.active} onClick={() => set('active', !form.active)}></button>
          </div>

          {err && <div className="error">{err}</div>}
        </div>
        <div className="panel-foot">
          <button type="button" className="btn primary" style={{ flex: 1, justifyContent: 'center' }}
            disabled={!canSave} onClick={() => trySave(false)}>{busy ? 'Сохраняем…' : 'Сохранить'}</button>
          <button type="button" className="btn" disabled={!canSave} onClick={() => trySave(true)}>Сохранить и добавить ещё</button>
        </div>
      </aside>
    </>
  );
}
